import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from "@angular/core";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";

export interface SharedTableColumn<T extends object = Record<string, unknown>> {
  key: Extract<keyof T, string>;
  header: string;
  sortable?: boolean;
  sortValue?: (row: T) => string | number;
}

@Component({
  selector: "app-shared-table",
  templateUrl: "./shared-table.component.html",
  styleUrls: ["./shared-table.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SharedTableComponent<
  T extends object = Record<string, unknown>,
> implements OnChanges {
  @Input() columns: SharedTableColumn<T>[] = [];
  @Input() data: T[] = [];
  @Input() emptyMessage = "No data available";
  @Input() ariaLabel = "Data table";
  @Input() pagination = false;
  @Input() pageSize = 25;
  @Input() cellTemplates: Partial<
    Record<Extract<keyof T, string>, TemplateRef<{ $implicit: T }>>
  > = {};
  @Output() rowClick = new EventEmitter<T>();

  @ContentChild("rowActions") rowActions?: TemplateRef<{ $implicit: T }>;

  private paginator?: MatPaginator;

  @ViewChild(MatPaginator)
  set tablePaginator(paginator: MatPaginator | undefined) {
    this.paginator = paginator;
    this.dataSource.paginator = paginator ?? null;
  }

  @ViewChild(MatSort)
  set tableSort(sort: MatSort) {
    this.dataSource.sort = sort;
  }

  readonly dataSource = new MatTableDataSource<T>([]);
  readonly actionsColumn = "__actions";

  constructor() {
    this.dataSource.sortingDataAccessor = (row, key) => {
      const column = this.columns.find((item) => item.key === key);
      if (!column) return "";
      const value = column.sortValue ? column.sortValue(row) : row[column.key];
      if (typeof value === "number") return value;
      if (typeof value === "boolean") return Number(value);
      return String(value ?? "").toLowerCase();
    };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["data"] || changes["pagination"] || changes["pageSize"]) {
      this.paginator?.firstPage();
      this.dataSource.data = this.data;
    }
  }

  get displayedColumns(): string[] {
    const keys = this.columns.map((c) => c.key);
    return this.rowActions ? [...keys, this.actionsColumn] : keys;
  }

  /** Rows are interactive only when a consumer listens to rowClick. */
  get clickable(): boolean {
    return this.rowClick.observed;
  }

  onRowKeydown(event: KeyboardEvent, row: T): void {
    if (!this.clickable || event.target !== event.currentTarget) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      this.rowClick.emit(row);
    }
  }
}
